import { Bug, Lightbulb, Zap, HelpCircle, FileText, CheckSquare } from 'lucide-react';
import { IssueType, TaskStatus } from '@/types';
import {
  type CategoryStyle,
} from '@/shared/utils/categoryStyles';

export type { CategoryStyle };
export { getCategoryColor, getCategoryStyle } from '@/shared/utils/categoryStyles';

/**
 * Issue type configuration with icons, colors, and labels
 */
export const ISSUE_TYPE_CONFIG: Record<
  IssueType,
  { icon: React.FC<any>; color: string; label: string; bgcolor: string }
> = {
  bug: {
    icon: Bug,
    color: '#dc2626',
    bgcolor: '#fee2e2',
    label: 'Bug',
  },
  feature: {
    icon: Lightbulb,
    color: '#16a34a',
    bgcolor: '#d1fae5',
    label: 'Feature',
  },
  improvement: {
    icon: Zap,
    color: '#2563eb',
    bgcolor: '#fef3c7',
    label: 'Improvement',
  },
  task: {
    icon: FileText,
    color: '#6b7280',
    bgcolor: '#dbeafe',
    label: 'Task',
  },
  question: {
    icon: HelpCircle,
    color: '#9333ea',
    bgcolor: '#f3f4f6',
    label: 'Question',
  },
};

/**
 * Category styling configuration (legacy presets; dynamic values use getCategoryStyle).
 */
export const CATEGORY_STYLES: Record<string, CategoryStyle> = {
  task: { bgcolor: '#dbeafe', color: '#1e40af', label: 'Task' },
  subtask: { bgcolor: '#e0f2fe', color: '#0369a1', label: 'Subtask' },
  development: { bgcolor: '#dbeafe', color: '#2563eb', label: 'Development' },
  design: { bgcolor: '#f3e8ff', color: '#9333ea', label: 'Design' },
  testing: { bgcolor: '#fef3c7', color: '#d97706', label: 'Testing' },
  documentation: { bgcolor: '#e0e7ff', color: '#4f46e5', label: 'Documentation' },
  research: { bgcolor: '#fce7f3', color: '#db2777', label: 'Research' },
  meeting: { bgcolor: '#cffafe', color: '#0891b2', label: 'Meeting' },
  review: { bgcolor: '#fef9c3', color: '#ca8a04', label: 'Review' },
  support: { bgcolor: '#fee2e2', color: '#dc2626', label: 'Support' },
  maintenance: { bgcolor: '#f5f5f4', color: '#57534e', label: 'Maintenance' },
  deployment: { bgcolor: '#d1fae5', color: '#059669', label: 'Deployment' },
  planning: { bgcolor: '#e0f2fe', color: '#0284c7', label: 'Planning' },
  other: { bgcolor: '#f3f4f6', color: '#6b7280', label: 'Other' },
};

/**
 * Category colors for simpler displays (ProjectTasksTab style)
 */
export const CATEGORY_COLORS: Record<string, string> = {
  task: '#1e40af',
  subtask: '#0369a1',
  development: '#2563eb',
  design: '#8b5cf6',
  testing: '#16a34a',
  documentation: '#0891b2',
  research: '#7c3aed',
  meeting: '#f59e0b',
  review: '#ec4899',
  support: '#14b8a6',
  maintenance: '#6366f1',
  deployment: '#ef4444',
  planning: '#64748b',
  other: '#9ca3af',
};

/**
 * Get deadline styling based on due date and status
 */
export const getDeadlineStyle = (dueDate: string, status: TaskStatus) => {
  if (status === TaskStatus.COMPLETED) {
    return { color: 'text.secondary' };
  }

  const now = new Date();
  const deadline = new Date(dueDate);
  const hoursUntil = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);

  if (hoursUntil < 0) {
    return { color: 'error.main', fontWeight: 600 };
  }
  if (hoursUntil < 24) {
    return { color: 'warning.main', fontWeight: 500 };
  }
  return { color: 'text.secondary' };
};

/**
 * RACI role priority for display (higher number = higher priority)
 */
export const RACI_ROLE_PRIORITY = {
  responsible: 4,
  accountable: 3,
  consulted: 2,
  informed: 1,
};

/**
 * RACI role colors and labels
 */
export const RACI_ROLE_CONFIG = {
  responsible: {
    label: 'R',
    fullLabel: 'Responsible',
    color: '#2563eb',
    bgColor: '#dbeafe',
  },
  accountable: {
    label: 'A',
    fullLabel: 'Accountable',
    color: '#7c3aed',
    bgColor: '#f3e8ff',
  },
  consulted: {
    label: 'C',
    fullLabel: 'Consulted',
    color: '#0891b2',
    bgColor: '#cffafe',
  },
  informed: {
    label: 'I',
    fullLabel: 'Informed',
    color: '#64748b',
    bgColor: '#f1f5f9',
  },
};

/**
 * Mobile breakpoint for responsive behavior
 */
export const MOBILE_BREAKPOINT = 'sm'; // theme.breakpoints.down('sm')

/**
 * Timer active background color
 */
export const TIMER_ACTIVE_BG = 'success.50';

/**
 * Blocked task background color
 */
export const BLOCKED_TASK_BG = 'rgba(245, 158, 11, 0.05)';
